/**
 * DailyLifeCard 組件
 *
 * 每日全人生活教練卡片，顯示：
 * - 今日飲食建議（早/午/晚餐）- 與 WeeklyLifeCard 同步
 * - 今日穿搭建議（幸運色與今日幸運元素一致）
 * - 學習主題（含系列進度）
 * - 運動/休閒建議（週末顯示週末活動）
 * - 每日自省問題（衍生自本週靜思重點）
 * - 節氣飲品（如有）
 *
 * @module components/DailyLifeCard
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  Box,
  Chip,
  Alert,
  CircularProgress,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  Tooltip,
  Popover,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Restaurant as FoodIcon,
  Checkroom as ClothingIcon,
  MenuBook as LearningIcon,
  FitnessCenter as ExerciseIcon,
  SelfImprovement as ReflectionIcon,
  Refresh as RefreshIcon,
  CheckCircleOutline as CompleteIcon,
  AutoAwesome as AiIcon,
  Weekend as WeekendIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  InfoOutlined as InfoOutlinedIcon,
  Casino as NumberIcon,
  Explore as DirectionIcon,
  Palette as ColorIcon,
  Category as ShapeIcon,
} from '@mui/icons-material';
import {
  getDailyLifeAdvice,
  completeTodayLearning,
  DailyLifeResponse,
  DailyFoodItem,
  DailyClothingItem,
  DailyExerciseItem,
  DailyReflectionItem,
  DailyLearningTopic,
  DailyPalaceInsight,
  getSeasonLabel,
  getPalaceColor,
} from '../api/dailyLife';
import {
  DailyFortuneResponse,
  formatLuckyNumbers,
  getDirectionEmoji,
  getShapeEmoji,
  getContrastTextColor,
  getColorGradient,
} from '../api/dailyfortune';
import { WeeklyAdviceResponse, MealItem, ReflectionItem, WeekendActivity } from '../api/weeklylife';

// ================================================================
// 型別
// ================================================================

interface DailyLifeCardProps {
  autoLoad?: boolean;
  targetDate?: string;
  onLoad?: (data: DailyLifeResponse) => void;
  /** 本週生活建議（從 WeeklyLifeCard hoist 而來，用於同步飲食/自省/週末運動） */
  weeklyAdvice?: WeeklyAdviceResponse | null;
  /** 今日幸運元素（從 DashboardPage 傳入，用於顯示幸運數字/顏色/方位/形狀） */
  fortune?: DailyFortuneResponse | null;
}

/** 統一餐點顯示型別 */
interface NormalizedMeal {
  name: string;
  calories?: number | null;
  description?: string | null;
}

/** 統一自省顯示型別 */
interface NormalizedReflection {
  question: string;
  category?: string;
  prompt_detail?: string | null;
  example_answer?: string | null;
}

// ================================================================
// Helper: 從 weeklyAdvice 取今日餐點
// ================================================================

const getTodayMealsFromWeekly = (
  weeklyAdvice: WeeklyAdviceResponse | null | undefined
): { breakfast: NormalizedMeal | null; lunch: NormalizedMeal | null; dinner: NormalizedMeal | null } | null => {
  if (!weeklyAdvice?.meals) return null;
  const jsDayOfWeek = new Date().getDay(); // 0=Sun,1=Mon,...,6=Sat
  const isWeekend = jsDayOfWeek === 0 || jsDayOfWeek === 6;

  const normalize = (meal: MealItem | null | undefined): NormalizedMeal | null => {
    if (!meal) return null;
    const m = meal as any;
    return {
      name: m.meal_name || m.main_dish || m.name || '未命名餐點',
      calories: m.estimated_kcal || m.estimated_kcal_avg || null,
      description: m.components || (m.drink ? `搭配飲品：${m.drink}` : m.description) || null,
    };
  };

  if (isWeekend) {
    return {
      breakfast: normalize(weeklyAdvice.meals.weekend_breakfast),
      lunch: normalize(weeklyAdvice.meals.weekend_lunch),
      dinner: normalize(weeklyAdvice.meals.weekend_dinner),
    };
  }

  // 工作日：jsDayOfWeek 1~5 → index 0~4
  const idx = jsDayOfWeek - 1;
  return {
    breakfast: normalize(weeklyAdvice.meals.weekday_breakfasts?.[idx]),
    lunch: normalize(weeklyAdvice.meals.weekday_lunches?.[idx]),
    dinner: normalize(weeklyAdvice.meals.weekday_dinners?.[idx]),
  };
};

/** 將 DailyFoodItem 轉為 NormalizedMeal */
const foodItemToNormalized = (item: DailyFoodItem | null): NormalizedMeal | null => {
  if (!item) return null;
  return { name: item.food_name, calories: item.calories_approx, description: item.description };
};

/** 從本週靜思重點取今日自省（依星期幾輪流） */
const getTodayReflectionFromWeekly = (
  reflections: ReflectionItem[] | null | undefined
): NormalizedReflection | null => {
  if (!reflections || reflections.length === 0) return null;
  const dayOfWeek = new Date().getDay();
  const r = reflections[dayOfWeek % reflections.length];
  return { question: r.question, category: r.category };
};

/** 將 DailyReflectionItem 轉為 NormalizedReflection */
const reflectionItemToNormalized = (item: DailyReflectionItem | null): NormalizedReflection | null => {
  if (!item) return null;
  return { question: item.question, category: item.category, prompt_detail: item.prompt_detail, example_answer: item.example_answer };
};

/** 今天是否為週末 */
const isWeekend = (): boolean => {
  const d = new Date().getDay();
  return d === 0 || d === 6;
};

// ================================================================
// 小型子元件
// ================================================================

const SectionHeader: React.FC<{
  icon: React.ReactNode;
  title: string;
  color: string;
  badge?: string;
}> = ({ icon, title, color, badge }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
    <Box sx={{ color, display: 'flex' }}>{icon}</Box>
    <Typography variant="subtitle1" fontWeight={700} color={color}>
      {title}
    </Typography>
    {badge && (
      <Chip label={badge} size="small" sx={{ fontSize: 10, height: 18, bgcolor: '#f3e5f5', color: '#7b1fa2' }} />
    )}
  </Box>
);

const NormalizedFoodSection: React.FC<{
  breakfast: NormalizedMeal | null;
  lunch: NormalizedMeal | null;
  dinner: NormalizedMeal | null;
}> = ({ breakfast, lunch, dinner }) => {
  const meals = [
    { label: '早餐', item: breakfast },
    { label: '午餐', item: lunch },
    { label: '晚餐', item: dinner },
  ];
  const hasAny = meals.some(m => m.item !== null);
  if (!hasAny) {
    return (
      <Typography variant="body2" color="text.secondary">
        今日飲食建議載入中，請先查看「本週生活建議」
      </Typography>
    );
  }
  return (
    <Box>
      {meals.map(({ label, item }) =>
        item ? (
          <Box key={label} sx={{ mb: 0.75 }}>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
              <Chip label={label} size="small" sx={{ fontSize: 11, height: 20 }} />
              <Typography variant="body2" fontWeight={600}>
                {item.name}
              </Typography>
              {item.calories && (
                <Typography variant="caption" color="text.secondary">
                  ~{item.calories} kcal
                </Typography>
              )}
            </Box>
            {item.description && (
              <Typography variant="caption" color="text.secondary" sx={{ pl: 0.5 }}>
                {item.description}
              </Typography>
            )}
          </Box>
        ) : null
      )}
    </Box>
  );
};

const ClothingSection: React.FC<{
  clothing: DailyClothingItem | null;
  fortuneLuckyColor?: string;
}> = ({ clothing, fortuneLuckyColor }) => {
  if (!clothing) return <Typography variant="body2" color="text.secondary">暫無穿搭建議</Typography>;

  const showLuckyColor = fortuneLuckyColor || clothing.ziwei_lucky_color;
  const isFortuneColor = !!fortuneLuckyColor;

  return (
    <Box>
      <Typography variant="body2" fontWeight={600}>
        {clothing.outfit_name}
      </Typography>
      <Box sx={{ mt: 0.5, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {clothing.top && <Chip label={`上：${clothing.top}`} size="small" variant="outlined" sx={{ fontSize: 11 }} />}
        {clothing.bottom && <Chip label={`下：${clothing.bottom}`} size="small" variant="outlined" sx={{ fontSize: 11 }} />}
        {clothing.shoes && <Chip label={`鞋：${clothing.shoes}`} size="small" variant="outlined" sx={{ fontSize: 11 }} />}
      </Box>
      {clothing.color_scheme && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
          色調：{clothing.color_scheme}
        </Typography>
      )}
      {showLuckyColor && (
        <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography variant="caption" color="text.secondary">
            今日幸運色：
          </Typography>
          <Chip
            label={showLuckyColor}
            size="small"
            sx={{ fontSize: 11, height: 18, bgcolor: isFortuneColor ? '#fff3e0' : undefined, fontWeight: 600 }}
          />
        </Box>
      )}
    </Box>
  );
};

const LearningSection: React.FC<{
  learning: DailyLearningTopic[];
  onComplete: (seriesId: number) => void;
  completing: number | null;
  completedSeries: Set<number>;
}> = ({ learning, onComplete, completing, completedSeries }) => {
  if (!learning || learning.length === 0) {
    return <Typography variant="body2" color="text.secondary">尚未開始任何學習系列，請至「我的學習旅程」選擇系列</Typography>;
  }
  return (
    <Box>
      {learning.map((topic, idx) => {
        const isCompleted = completedSeries.has(topic.series_id);
        const isCompleting = completing === topic.series_id;
        return (
          <Box key={topic.series_id} sx={{ mb: idx < learning.length - 1 ? 2 : 0 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  {topic.series_name} · 第 {topic.topic_order} 課
                </Typography>
                {topic.topic_name && (
                  <Typography variant="body2" fontWeight={600} sx={{ mt: 0.25 }}>
                    {topic.topic_name}
                  </Typography>
                )}
                {topic.is_new_series && (
                  <Chip label="新系列" size="small" color="primary" sx={{ fontSize: 11, height: 18, mt: 0.5 }} />
                )}
              </Box>
              {!isCompleted ? (
                <Tooltip title="標記今日學習完成，進度 +1">
                  <span>
                    <IconButton
                      size="small"
                      color="success"
                      onClick={() => onComplete(topic.series_id)}
                      disabled={completing !== null}
                    >
                      {isCompleting ? <CircularProgress size={16} /> : <CompleteIcon fontSize="small" />}
                    </IconButton>
                  </span>
                </Tooltip>
              ) : (
                <Chip label="已完成" size="small" color="success" sx={{ fontSize: 11 }} />
              )}
            </Box>
            {topic.content && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                {topic.content.length > 120 ? topic.content.slice(0, 120) + '...' : topic.content}
              </Typography>
            )}
            {topic.key_insight && (
              <Box sx={{ mt: 0.75, p: 1, bgcolor: 'primary.50', borderRadius: 1, borderLeft: '3px solid', borderColor: 'primary.main' }}>
                <Typography variant="caption" fontWeight={600} color="primary.main">核心洞見</Typography>
                <Typography variant="caption" display="block" color="text.secondary">{topic.key_insight}</Typography>
              </Box>
            )}
            {topic.action_step && (
              <Typography variant="caption" color="success.main" sx={{ mt: 0.5, display: 'block' }}>
                行動：{topic.action_step}
              </Typography>
            )}
          </Box>
        );
      })}
    </Box>
  );
};

const ExerciseSection: React.FC<{
  exercise: DailyExerciseItem | null;
  weekendActivities?: { static_activity: WeekendActivity | null; dynamic_activity: WeekendActivity | null } | null;
}> = ({ exercise, weekendActivities }) => {
  const today = isWeekend();

  if (today && weekendActivities && (weekendActivities.static_activity || weekendActivities.dynamic_activity)) {
    return (
      <Box>
        {weekendActivities.static_activity && (
          <Box sx={{ mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip label="靜態" size="small" color="info" sx={{ fontSize: 11, height: 20 }} />
              <Typography variant="body2" fontWeight={600}>{weekendActivities.static_activity.activity_name}</Typography>
              {weekendActivities.static_activity.duration && (
                <Chip label={weekendActivities.static_activity.duration} size="small" sx={{ fontSize: 11, height: 20 }} />
              )}
            </Box>
            {weekendActivities.static_activity.description && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.25, display: 'block' }}>
                {weekendActivities.static_activity.description}
              </Typography>
            )}
            {weekendActivities.static_activity.benefits && (
              <Typography variant="caption" color="success.main" sx={{ mt: 0.25, display: 'block' }}>
                益處：{weekendActivities.static_activity.benefits}
              </Typography>
            )}
          </Box>
        )}
        {weekendActivities.dynamic_activity && (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip label="動態" size="small" color="warning" sx={{ fontSize: 11, height: 20 }} />
              <Typography variant="body2" fontWeight={600}>{weekendActivities.dynamic_activity.activity_name}</Typography>
              {weekendActivities.dynamic_activity.duration && (
                <Chip label={weekendActivities.dynamic_activity.duration} size="small" sx={{ fontSize: 11, height: 20 }} />
              )}
            </Box>
            {weekendActivities.dynamic_activity.description && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.25, display: 'block' }}>
                {weekendActivities.dynamic_activity.description}
              </Typography>
            )}
            {weekendActivities.dynamic_activity.benefits && (
              <Typography variant="caption" color="success.main" sx={{ mt: 0.25, display: 'block' }}>
                益處：{weekendActivities.dynamic_activity.benefits}
              </Typography>
            )}
          </Box>
        )}
      </Box>
    );
  }

  if (!exercise) return <Typography variant="body2" color="text.secondary">暫無運動建議</Typography>;
  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="body2" fontWeight={600}>{exercise.activity_name}</Typography>
        {exercise.duration_min && (
          <Chip label={`${exercise.duration_min} 分鐘`} size="small" sx={{ fontSize: 11, height: 20 }} />
        )}
        {exercise.intensity_level && (
          <Chip label={exercise.intensity_level} size="small" variant="outlined" sx={{ fontSize: 11, height: 20 }} />
        )}
      </Box>
      {exercise.description && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.25, display: 'block' }}>
          {exercise.description}
        </Typography>
      )}
      {exercise.benefits && (
        <Typography variant="caption" color="success.main" sx={{ mt: 0.25, display: 'block' }}>
          效益：{exercise.benefits}
        </Typography>
      )}
    </Box>
  );
};

const NormalizedReflectionSection: React.FC<{ reflection: NormalizedReflection | null; fromWeekly?: boolean }> = ({ reflection, fromWeekly: _fromWeekly }) => {
  const [showExample, setShowExample] = useState(false);
  if (!reflection) return <Typography variant="body2" color="text.secondary">暫無靜思內容</Typography>;
  return (
    <Box>
      {reflection.category && (
        <Chip label={reflection.category} size="small" color="secondary" sx={{ mb: 0.5, fontSize: 11, height: 18 }} />
      )}
      <Typography variant="body2" fontWeight={600} sx={{ fontStyle: 'italic' }}>
        「{reflection.question}」
      </Typography>
      {reflection.prompt_detail && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
          {reflection.prompt_detail}
        </Typography>
      )}
      {reflection.example_answer && (
        <Box sx={{ mt: 0.5 }}>
          <Button
            size="small"
            variant="text"
            sx={{ fontSize: 11, p: 0, minWidth: 0, textTransform: 'none' }}
            onClick={() => setShowExample((v) => !v)}
          >
            {showExample ? '收起範例' : '查看範例'}
          </Button>
          {showExample && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.25, display: 'block', pl: 0.5 }}>
              {reflection.example_answer}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};


// ================================================================
// 今日幸運元素（原獨立卡片，現併入此處）
// ================================================================

const FortuneSection: React.FC<{ fortune: DailyFortuneResponse | null }> = ({ fortune }) => {
  if (!fortune?.success || !fortune.result) return null;
  const r = fortune.result;
  const colorHex = r.lucky_color_hex;
  const textColor = getContrastTextColor(colorHex);
  const colorGradient = getColorGradient(colorHex);

  return (
    <Box sx={{ mb: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
        <Typography variant="subtitle2" fontWeight={700} color="#5d4037">
          🎯 今日幸運元素
        </Typography>
        {fortune.cached && (
          <Chip label="快取" size="small" color="info" variant="outlined" sx={{ fontSize: 10, height: 18 }} />
        )}
      </Box>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        {/* 幸運數字 */}
        <Box sx={{
          flex: '1 1 auto', minWidth: 110, p: 1, borderRadius: 1,
          bgcolor: '#e3f2fd', border: '1px solid #bbdefb', textAlign: 'center',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, mb: 0.25 }}>
            <NumberIcon sx={{ fontSize: 14, color: '#1565c0' }} />
            <Typography variant="caption" fontWeight={600} color="#1565c0">幸運數字</Typography>
          </Box>
          <Typography variant="body1" fontWeight={700} color="#0d47a1">
            {formatLuckyNumbers(r.lucky_numbers)}
          </Typography>
        </Box>
        {/* 幸運方位 */}
        <Box sx={{
          flex: '1 1 auto', minWidth: 90, p: 1, borderRadius: 1,
          bgcolor: '#e8f5e9', border: '1px solid #c8e6c9', textAlign: 'center',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, mb: 0.25 }}>
            <DirectionIcon sx={{ fontSize: 14, color: '#2e7d32' }} />
            <Typography variant="caption" fontWeight={600} color="#2e7d32">幸運方位</Typography>
          </Box>
          <Typography variant="body1" fontWeight={700} color="#1b5e20">
            {getDirectionEmoji(r.lucky_direction)} {r.lucky_direction}
          </Typography>
        </Box>
        {/* 幸運顏色 */}
        <Box sx={{
          flex: '1 1 auto', minWidth: 90, p: 1, borderRadius: 1,
          background: colorGradient, color: textColor, textAlign: 'center',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, mb: 0.25 }}>
            <ColorIcon sx={{ fontSize: 14 }} />
            <Typography variant="caption" fontWeight={600}>幸運顏色</Typography>
          </Box>
          <Typography variant="body1" fontWeight={700}>
            {r.lucky_color_nearest}
          </Typography>
        </Box>
        {/* 幸運形狀 */}
        <Box sx={{
          flex: '1 1 auto', minWidth: 90, p: 1, borderRadius: 1,
          bgcolor: '#fff8e1', border: '1px solid #ffe082', textAlign: 'center',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, mb: 0.25 }}>
            <ShapeIcon sx={{ fontSize: 14, color: '#f57f17' }} />
            <Typography variant="caption" fontWeight={600} color="#f57f17">幸運形狀</Typography>
          </Box>
          <Typography variant="body1" fontWeight={700} color="#e65100">
            {getShapeEmoji(r.lucky_shape)} {r.lucky_shape}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};


// ================================================================
// 今日宮位運勢提示
// ================================================================

const PalaceInsightSection: React.FC<{ insight: DailyPalaceInsight | null }> = ({ insight }) => {
  const [infoAnchor, setInfoAnchor] = useState<HTMLButtonElement | null>(null);
  if (!insight) return null;
  const { best, worst } = insight;
  return (
    <Box sx={{ mb: 1.5 }}>
      {/* 說明 icon（右上角） */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: -1 }}>
        <IconButton onClick={e => setInfoAnchor(e.currentTarget)} sx={{ opacity: 0.6, p: 0.5 }}>
          <InfoOutlinedIcon sx={{ fontSize: 20 }} />
        </IconButton>
        <Popover
          open={Boolean(infoAnchor)}
          anchorEl={infoAnchor}
          onClose={() => setInfoAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Box sx={{ p: 1.5, maxWidth: 220 }}>
            <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" gutterBottom>
              宮位分數說明
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block">
              通常介於 -10 至 +10（極少數情況會超出此範圍）
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
              分數越高，當日這個宮位的運勢越旺
            </Typography>
          </Box>
        </Popover>
      </Box>
      {/* 最佳宮 */}
      <Box
        sx={{
          display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1,
          p: 1.5, borderRadius: 1, bgcolor: '#e8f5e9', border: '1px solid #c8e6c9',
        }}
      >
        <TrendingUpIcon sx={{ color: '#2e7d32', mt: 0.25, flexShrink: 0 }} />
        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
            <Typography variant="subtitle2" fontWeight={700} color="#2e7d32">
              今日最旺：{best.palace}
            </Typography>
            <Chip label={best.theme} size="small"
              sx={{ fontSize: 10, height: 18, bgcolor: '#a5d6a7', color: '#1b5e20' }} />
            <Typography variant="caption" color="#388e3c" sx={{ ml: 'auto' }}>
              {best.score} 分
            </Typography>
          </Box>
          <Typography variant="body2" color="#33691e">
            {best.advice}
          </Typography>
        </Box>
      </Box>

      {/* 最差宮 */}
      <Box
        sx={{
          display: 'flex', alignItems: 'flex-start', gap: 1,
          p: 1.5, borderRadius: 1, bgcolor: '#fff3e0', border: '1px solid #ffe0b2',
        }}
      >
        <TrendingDownIcon sx={{ color: '#e65100', mt: 0.25, flexShrink: 0 }} />
        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
            <Typography variant="subtitle2" fontWeight={700} color="#e65100">
              今日留心：{worst.palace}
            </Typography>
            <Chip label={worst.theme} size="small"
              sx={{ fontSize: 10, height: 18, bgcolor: '#ffcc80', color: '#bf360c' }} />
            <Typography variant="caption" color="#f57c00" sx={{ ml: 'auto' }}>
              {worst.score} 分
            </Typography>
          </Box>
          <Typography variant="body2" color="#bf360c">
            {worst.advice}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};


// ================================================================
// 主元件
// ================================================================

const DailyLifeCard: React.FC<DailyLifeCardProps> = ({
  autoLoad = true,
  targetDate,
  onLoad,
  weeklyAdvice,
  fortune,
}) => {
  const [data, setData] = useState<DailyLifeResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [completing, setCompleting] = useState<number | null>(null);
  const [completedSeries, setCompletedSeries] = useState<Set<number>>(new Set());
  const [completeMsg, setCompleteMsg] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getDailyLifeAdvice(targetDate);
      setData(result);
      onLoad?.(result);
    } catch (e: any) {
      const msg =
        e?.response?.data?.error ??
        e?.response?.data?.message ??
        '無法載入每日建議，請稍後再試';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [targetDate, onLoad]);

  useEffect(() => {
    if (autoLoad) loadData();
  }, [autoLoad, loadData]);

  const handleCompleteLeaning = async (seriesId: number) => {
    setCompleting(seriesId);
    setCompleteMsg(null);
    try {
      const res = await completeTodayLearning(seriesId);
      setCompletedSeries(prev => new Set(prev).add(seriesId));
      setCompleteMsg(res.message);
    } catch {
      setCompleteMsg('更新進度失敗，請稍後再試');
    } finally {
      setCompleting(null);
    }
  };

  const palaceColor = data ? getPalaceColor(data.lowest_palace) : '#757575';

  // 計算今日餐點：優先使用 weeklyAdvice（與本週生活建議一致）
  const weeklyMeals = getTodayMealsFromWeekly(weeklyAdvice);
  const todayBreakfast = weeklyMeals?.breakfast ?? (data ? foodItemToNormalized(data.daily.breakfast) : null);
  const todayLunch = weeklyMeals?.lunch ?? (data ? foodItemToNormalized(data.daily.lunch) : null);
  const todayDinner = weeklyMeals?.dinner ?? (data ? foodItemToNormalized(data.daily.dinner) : null);

  // 計算今日自省：優先使用 weeklyAdvice 靜思重點
  const weeklyReflection = getTodayReflectionFromWeekly(weeklyAdvice?.reflections);
  const dailyReflection = data ? reflectionItemToNormalized(data.daily.reflection) : null;
  const todayReflection: NormalizedReflection | null = weeklyReflection ?? dailyReflection;

  // 週末活動：週末時顯示 weeklyAdvice 的活動
  const weekendActivities = isWeekend() ? weeklyAdvice?.activities ?? null : null;

  // 從 fortune 取得幸運顏色（供穿搭區塊使用）
  const fortuneLuckyColor = fortune?.result?.lucky_color_nearest;

  return (
    <Card sx={{ mb: 2, borderTop: `4px solid ${palaceColor}` }}>
      <CardHeader
        avatar={<AiIcon sx={{ color: palaceColor }} />}
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h6" component="span">今日全人生活建議</Typography>
            {data && (
              <Tooltip title={`根據您命盤中 ${data.lowest_palace} 的運勢，提供對應的生活補強建議（${getSeasonLabel(data.season)}季）`}>
                <Chip
                  label={`今日重點：${data.lowest_palace}`}
                  size="small"
                  sx={{ bgcolor: palaceColor, color: '#fff', fontSize: 11, cursor: 'help' }}
                />
              </Tooltip>
            )}
          </Box>
        }
        subheader={data ? `${data.date}${data.palace_insight ? ` · 流日命宮：${data.palace_insight.flow_day_palace}（農曆${data.palace_insight.lunar_day}日）` : ''} · 紫微命盤個人化` : '根據紫微命盤個人化'}
        action={
          <IconButton onClick={loadData} disabled={loading} size="small">
            <RefreshIcon />
          </IconButton>
        }
      />

      <CardContent sx={{ pt: 0 }}>
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={32} />
            </Box>
          )}

          {error && !loading && (
            <Alert severity="warning" sx={{ mb: 1 }}>
              {error}
              <Button size="small" onClick={loadData} sx={{ ml: 1 }}>
                重試
              </Button>
            </Alert>
          )}

          {completeMsg && (
            <Alert severity="success" onClose={() => setCompleteMsg(null)} sx={{ mb: 1 }}>
              {completeMsg}
            </Alert>
          )}

          {/* 飲食（即使 data 未載入，只要 weeklyAdvice 有資料就能顯示） */}
          {(data || weeklyMeals) && !loading && (
            <>
              {/* 今日幸運元素（置頂） */}
              <FortuneSection fortune={fortune ?? null} />

              {/* 今日宮位運勢提示 */}
              <PalaceInsightSection insight={data?.palace_insight ?? null} />

              {/* 飲食 */}
              <Accordion disableGutters elevation={0}
                sx={{ '&:before': { display: 'none' }, border: '1px solid', borderColor: 'divider', borderRadius: 1, mb: 1 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 44, py: 0 }}>
                  <SectionHeader icon={<FoodIcon fontSize="small" />} title="今日飲食" color="#e65100" />
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0 }}>
                  <NormalizedFoodSection
                    breakfast={todayBreakfast}
                    lunch={todayLunch}
                    dinner={todayDinner}
                  />
                </AccordionDetails>
              </Accordion>

              {/* 穿搭 */}
              <Accordion disableGutters elevation={0}
                sx={{ '&:before': { display: 'none' }, border: '1px solid', borderColor: 'divider', borderRadius: 1, mb: 1 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 44, py: 0 }}>
                  <SectionHeader icon={<ClothingIcon fontSize="small" />} title="今日穿搭" color="#7b1fa2" />
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0 }}>
                  <ClothingSection clothing={data?.daily.clothing ?? null} fortuneLuckyColor={fortuneLuckyColor} />
                </AccordionDetails>
              </Accordion>

              {/* 學習 */}
              <Accordion disableGutters elevation={0}
                sx={{ '&:before': { display: 'none' }, border: '1px solid', borderColor: 'divider', borderRadius: 1, mb: 1 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 44, py: 0 }}>
                  <SectionHeader icon={<LearningIcon fontSize="small" />} title="今日學習" color="#1565c0" />
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0 }}>
                  <LearningSection
                    learning={data?.daily.learning ?? []}
                    onComplete={handleCompleteLeaning}
                    completing={completing}
                    completedSeries={completedSeries}
                  />
                </AccordionDetails>
              </Accordion>

              {/* 運動 */}
              <Accordion disableGutters elevation={0}
                sx={{ '&:before': { display: 'none' }, border: '1px solid', borderColor: 'divider', borderRadius: 1, mb: 1 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 44, py: 0 }}>
                  <SectionHeader
                    icon={isWeekend() && weekendActivities ? <WeekendIcon fontSize="small" /> : <ExerciseIcon fontSize="small" />}
                    title={isWeekend() && weekendActivities ? '今日運動（週末活動）' : '今日運動'}
                    color="#2e7d32"
                  />
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0 }}>
                  <ExerciseSection exercise={data?.daily.exercise ?? null} weekendActivities={weekendActivities} />
                </AccordionDetails>
              </Accordion>

              {/* 自省 */}
              <Accordion disableGutters elevation={0}
                sx={{ '&:before': { display: 'none' }, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 44, py: 0 }}>
                  <SectionHeader icon={<ReflectionIcon fontSize="small" />} title="今日靜思" color="#4527a0" />
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0 }}>
                  <NormalizedReflectionSection reflection={todayReflection} fromWeekly={false} />
                </AccordionDetails>
              </Accordion>
            </>
          )}

          {!data && !weeklyMeals && !loading && !error && (
            <Alert severity="info">建議資料載入中，請稍候...</Alert>
          )}
        </CardContent>
    </Card>
  );
};

export default DailyLifeCard;
